import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from 'pages/guide/components/GuideInfoBox';
import GuidePageLayout from 'pages/guide/components/GuidePageLayout';
import GuideSection from 'pages/guide/components/GuideSection';
import GuideTable from 'pages/guide/components/GuideTable';

const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
  <Box
    component="pre"
    sx={{
      bgcolor: 'action.hover',
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      overflow: 'auto',
      fontSize: '0.8rem',
      lineHeight: 1.6,
      fontFamily: 'monospace',
    }}
  >
    <code>{children}</code>
  </Box>
);

const GuideFrontendDeploy: React.FC = () => {
  return (
    <GuidePageLayout
      title="フロントエンド 手動デプロイ手順"
      description="フロントエンドアプリケーションをS3 + CloudFrontに手動でデプロイする手順です。"
      breadcrumbs={[{ label: 'デプロイ', path: '/guide/deploy/overview' }]}
    >
      <GuideInfoBox type="warning">
        本手順はDEVELOPER権限を持つユーザーのみ実行できます。認証情報は別途管理されているドキュメントを参照してください。
      </GuideInfoBox>

      <GuideSection title="前提条件">
        <Stack spacing={1.5}>
          <GuideTable
            headers={['項目', '要件']}
            rows={[
              ['AWS CLI', 'v2 がインストール済み'],
              ['Node.js', 'v20 以上'],
              ['AWS認証', 'ap-northeast-1 リージョンで認証済み（aws configure または SSO）'],
              ['リポジトリ', 'ardito-inhouse-kanzhong-front をローカルにクローン済み'],
            ]}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="アーキテクチャ">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            フロントエンドは以下の構成でホスティングされています。
          </Typography>
          <GuideTable
            headers={['コンポーネント', '役割']}
            rows={[
              ['S3 バケット', 'ビルド成果物（HTML/JS/CSS）の格納先'],
              ['CloudFront', 'CDN配信、HTTPS終端、SPAフォールバック'],
              ['Route 53', 'portal.kanzhong.app のDNSレコード管理'],
              ['ACM', 'SSL/TLS証明書（自動更新）'],
            ]}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="手順">
        <Stack spacing={2.5}>
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 1: リポジトリを最新化
            </Typography>
            <CodeBlock>{`cd ardito-inhouse-kanzhong-front
git checkout main
git pull origin main`}</CodeBlock>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 2: 依存関係のインストール
            </Typography>
            <CodeBlock>{`npm ci`}</CodeBlock>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 3: 本番ビルド
            </Typography>
            <Typography variant="body2" color="text.secondary">
              env/prod.env の環境変数を読み込んでビルドします。
            </Typography>
            <CodeBlock>{`npm run build`}</CodeBlock>
            <GuideInfoBox>
              ビルド完了後「The project was built assuming it is hosted at
              /.」と表示されれば正常です。build/ ディレクトリに成果物が生成されます。
            </GuideInfoBox>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 4: S3 にアップロード
            </Typography>
            <Typography variant="body2" color="text.secondary">
              既存のファイルと同期し、不要なファイルは削除します。
            </Typography>
            <CodeBlock>{`aws s3 sync build/ s3://<S3_BUCKET> --delete`}</CodeBlock>
            <GuideInfoBox type="warning">
              --delete オプションにより、S3上にあってローカルにないファイルは削除されます。 build/
              ディレクトリの内容が正しいことを確認してから実行してください。
            </GuideInfoBox>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 5: CloudFront キャッシュ無効化
            </Typography>
            <Typography variant="body2" color="text.secondary">
              CloudFront のキャッシュをクリアして最新のファイルを配信します。
            </Typography>
            <CodeBlock>{`aws cloudfront create-invalidation \\
  --distribution-id <CLOUDFRONT_DISTRIBUTION_ID> \\
  --paths '/*'`}</CodeBlock>
            <GuideInfoBox>
              キャッシュ無効化は完了まで1〜5分程度かかります。ステータスは AWS コンソールの
              CloudFront → Invalidations タブで確認できます。
            </GuideInfoBox>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 6: 動作確認
            </Typography>
            <CodeBlock>{`curl -I https://portal.kanzhong.app`}</CodeBlock>
            <Typography variant="body2" color="text.secondary">
              HTTP 200 が返ることを確認し、ブラウザで画面が正常に表示されることを確認します。
            </Typography>
          </Stack>
        </Stack>
      </GuideSection>

      <GuideSection title="ワンライナー（環境変数設定済みの場合）">
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            環境変数 S3_BUCKET, CLOUDFRONT_DISTRIBUTION_ID
            が設定されていれば、以下のコマンドで一括実行可能です。
          </Typography>
          <CodeBlock>{`npm run deploy`}</CodeBlock>
        </Stack>
      </GuideSection>

      <GuideSection title="自動デプロイ（GitHub Actions）">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            通常のデプロイは main ブランチへのプッシュで GitHub Actions
            が自動実行します。手動デプロイは緊急時やデバッグ時のみ使用してください。
          </Typography>
          <GuideTable
            headers={['トリガー', '処理内容']}
            rows={[
              ['main ブランチへのプッシュ', 'npm ci → build → S3 sync → CloudFront invalidation'],
            ]}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="トラブルシューティング">
        <Stack spacing={1.5}>
          <GuideTable
            headers={['症状', '原因', '対処']}
            rows={[
              [
                'デプロイ後に画面が白くなる',
                'CloudFront キャッシュが残っている',
                'キャッシュ無効化を実行し、ブラウザのキャッシュもクリア',
              ],
              [
                'リロードで 403/404 になる',
                'CloudFront のエラーレスポンス設定が欠落',
                'カスタムエラーレスポンス（403/404 → /index.html）を確認',
              ],
              [
                'API が繋がらない',
                'CORS設定またはバックエンドURL設定の不備',
                'REACT_APP_KANZHONG_BACKEND_HOST の値とバックエンドCORS設定を確認',
              ],
              [
                'S3 sync で Access Denied',
                'AWS認証が切れているまたは権限不足',
                'aws sts get-caller-identity で認証状態を確認',
              ],
            ]}
          />
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideFrontendDeploy;
